import { useState } from 'react';
import { THEME_CATEGORIES } from '~/lib/plugin-sdk';
import type { InstallView, ListingCard as ListingCardData } from '@dunea/shared';
import { apiGetListing, apiListFirstPartyThemes, type ListingSort } from '~/lib/api/marketplace';
import { classNames } from '~/utils/classNames';
import {
  BTN_SECONDARY,
  CARD,
  ErrorBanner,
  LoadError,
  Section,
  SkeletonCard,
  Spinner,
} from '~/components/workbench/tools/payments/ui';
import { useResource } from '~/components/workbench/tools/payments/use-resource';
import { CatalogControls } from './BrowseView';
import { installListing, requestApplyTheme } from './install-actions';
import {
  apiErrorMessage,
  categoryLabel,
  coerceThemeTokens,
  installFor,
  normalizeFirstPartyThemes,
  priceLabel,
  type ThemeEntry,
} from './marketplace-model';
import { CategoryChips, DeveloperLine, ThemePreview } from './parts';
import { useListings } from './use-listings';

const CATEGORY_OPTIONS = THEME_CATEGORIES.map((c) => [c, categoryLabel(c)] as const);

/** Themes: Dunea's own (apply directly) and marketplace themes (install, then apply). */
export function ThemesView({
  appId,
  installs,
  onInstalled,
  onOpen,
}: {
  appId: string;
  installs: InstallView[] | null;
  onInstalled: () => void;
  onOpen: (slug: string) => void;
}) {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [sort, setSort] = useState<ListingSort>('popular');
  const firstParty = useResource(async () => normalizeFirstPartyThemes(await apiListFirstPartyThemes()), 'first-party');
  const list = useListings('theme', { q, category, sort });

  const needle = q.trim().toLowerCase();
  const ownThemes = (firstParty.data ?? []).filter(
    (t) =>
      (!category || t.categories.includes(category)) &&
      (!needle || t.name.toLowerCase().includes(needle) || t.tagline.toLowerCase().includes(needle)),
  );

  return (
    <div className="space-y-4">
      <CatalogControls q={q} onQ={setQ} sort={sort} onSort={setSort} placeholder="Search themes" />
      <CategoryChips categories={CATEGORY_OPTIONS} value={category} onChange={setCategory} label="Theme categories" />

      <Section title="By Dunea" icon="i-ph:paint-brush">
        {firstParty.error ? (
          <LoadError message="Could not load Dunea's themes." onRetry={() => void firstParty.reload()} />
        ) : !firstParty.data ? (
          <ThemeGridSkeleton />
        ) : ownThemes.length === 0 ? (
          <Empty text={firstParty.data.length ? 'No Dunea themes match.' : 'No Dunea themes yet.'} />
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2.5">
            {ownThemes.map((t) => (
              <FirstPartyThemeCard key={t.id} theme={t} />
            ))}
          </div>
        )}
      </Section>

      <Section
        title="Community"
        icon="i-ph:users-three"
        aside={(list.loading || list.settling) && <Spinner className="text-[11px]" />}
      >
        {list.error && !list.loading ? (
          <LoadError message="Could not load themes." onRetry={() => void list.reload()} />
        ) : list.loading && list.items.length === 0 ? (
          <ThemeGridSkeleton />
        ) : list.items.length === 0 ? (
          <Empty text="No community themes match yet." />
        ) : (
          <>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2.5">
              {list.items.map((item) => (
                <MarketplaceThemeCard
                  key={item.slug}
                  appId={appId}
                  item={item}
                  install={installFor(installs, item.slug)}
                  onInstalled={onInstalled}
                  onOpen={() => onOpen(item.slug)}
                />
              ))}
            </div>
            {list.hasMore && (
              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  onClick={() => void list.loadMore()}
                  disabled={list.loadingMore}
                  className={BTN_SECONDARY}
                >
                  {list.loadingMore && <Spinner />}
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </Section>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div className={classNames(CARD, 'px-4 py-6 text-center text-[12px] text-muted')}>{text}</div>;
}

function ThemeGridSkeleton() {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2.5">
      <SkeletonCard lines={4} />
      <SkeletonCard lines={4} />
    </div>
  );
}

function FirstPartyThemeCard({ theme }: { theme: ThemeEntry }) {
  return (
    <div className={classNames(CARD, 'flex flex-col gap-3 p-3')}>
      <ThemePreview tokens={theme.tokens} name={theme.name} />
      <div className="min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-[13px] font-medium text-primary">{theme.name}</span>
          <span className="shrink-0 text-[11px] text-muted">Free</span>
        </div>
        {theme.tagline && <p className="mt-0.5 line-clamp-2 text-[11.5px] text-secondary">{theme.tagline}</p>}
      </div>
      <button type="button" onClick={() => requestApplyTheme(theme.name, theme.id)} className={BTN_SECONDARY}>
        <div className="i-ph:magic-wand text-[13px]" />
        Apply to this app
      </button>
    </div>
  );
}

function MarketplaceThemeCard({
  appId,
  item,
  install,
  onInstalled,
  onOpen,
}: {
  appId: string;
  item: ListingCardData;
  install: InstallView | null;
  onInstalled: () => void;
  onOpen: () => void;
}) {
  // ListingCard carries no tokens, so each card fetches its detail for the preview.
  const detail = useResource(() => apiGetListing(item.slug), item.slug);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tokens = detail.data?.theme ? coerceThemeTokens(detail.data.theme.tokens) : null;

  const apply = async () => {
    if (install?.status === 'active') {
      requestApplyTheme(item.name, item.slug);
      return;
    }

    if (install?.status === 'pending_payment' || install?.status === 'suspended') {
      onOpen();
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const result = await installListing(appId, { slug: item.slug, permissions: [], source: 'workbench' });

      if (result !== 'redirecting') {
        onInstalled();
        requestApplyTheme(item.name, item.slug);
      }
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not install this theme.'));
    } finally {
      setBusy(false);
    }
  };

  const label =
    install?.status === 'active'
      ? 'Apply to this app'
      : item.pricing.model === 'free'
        ? 'Install and apply'
        : `Buy · ${priceLabel(item.pricing)}`;

  return (
    <div className={classNames(CARD, 'flex flex-col gap-3 p-3')}>
      <button type="button" onClick={onOpen} className="text-left" aria-label={`${item.name} details`}>
        {tokens ? (
          <ThemePreview tokens={tokens} name={item.name} />
        ) : (
          <div className="flex h-[150px] items-center justify-center rounded-lg border border-hairline bg-sunken">
            {detail.loading ? <Spinner /> : <span className="text-[11px] text-muted">No preview</span>}
          </div>
        )}
      </button>
      <div className="min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate text-[13px] font-medium text-primary">{item.name}</span>
          <span className="shrink-0 text-[11px] text-muted">{priceLabel(item.pricing)}</span>
        </div>
        <DeveloperLine developer={item.developer} />
      </div>
      <button type="button" onClick={() => void apply()} disabled={busy} className={BTN_SECONDARY}>
        {busy ? <Spinner /> : <div className="i-ph:magic-wand text-[13px]" />}
        {label}
      </button>
      {error && <ErrorBanner message={error} />}
    </div>
  );
}
